// 設定 → 安全性：兩步驟驗證（plan/vms-web-v0.md AUTH-03）。工作帳號強制、不能停用。
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { mfaDisable, mfaStatus, regenerateRecoveryCodes, SessionError, totpConfirm, totpSetupSignedIn, type TotpSetup } from '../../lib/session';
import { KinioError } from '@kinio/web-sdk';
import { RecoveryCodes, TotpQr } from '../../components/TotpQr';
import { Dialog } from '../../components/Dialog';
import { PasswordInput } from '../../components/PasswordInput';
import { EmailVerifyPrompt, emailVerifyRequired } from '../../components/EmailVerifyPrompt';

type Mode = null | 'enable' | 'disable' | 'recovery';

export function SecuritySection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const st = useQuery({ queryKey: ['mfa-status'], queryFn: () => mfaStatus() });
  const [mode, setMode] = useState<Mode>(null);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** 403 EMAIL_NOT_VERIFIED →「先驗證你的 email」（kq Q143）；處理了回 true。 */
  const [emailVerify, setEmailVerify] = useState<{ resend: boolean } | null>(null);
  const askEmailVerify = (e: unknown) => {
    const ev = emailVerifyRequired(e);
    if (ev) setEmailVerify(ev);
    return ev !== null;
  };

  function reset() {
    setMode(null);
    setSetup(null);
    setCodes(null);
    setCode('');
    setPassword('');
    setErr(null);
    void qc.invalidateQueries({ queryKey: ['mfa-status'] });
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (x) {
      if (askEmailVerify(x)) return setMode(null); // 開啟對話框收起來，改跳「先驗證你的 email」
      const c = x instanceof SessionError || x instanceof KinioError ? x.code : 'default';
      setErr(t(`mfa.err_${c}`, { code: c, defaultValue: t('mfa.err_default', { code: c }) }));
    } finally {
      setBusy(false);
    }
  }

  async function startEnable() {
    setMode('enable');
    await run(async () => setSetup(await totpSetupSignedIn()));
  }

  function confirmEnable(e: FormEvent) {
    e.preventDefault();
    void run(async () => setCodes((await totpConfirm(code)).recovery_codes));
  }

  function submitDisable(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await mfaDisable(password, code);
      reset();
    });
  }

  function submitRecovery(e: FormEvent) {
    e.preventDefault();
    void run(async () => setCodes((await regenerateRecoveryCodes(code)).recovery_codes));
  }

  const s = st.data;
  return (
    <section className="card">
      <h2 className="section-title">{t('mfa.section')}</h2>
      {st.isLoading && <p className="muted">{t('common.loading')}</p>}
      {st.isError && <p className="error">{t('common.error')}</p>}
      {s && (
        <>
          <p>
            {s.totp_enabled ? (
              <span className="badge badge-ok">{t('mfa.enabled')}</span>
            ) : (
              <span className="badge badge-warn">{t('mfa.disabled')}</span>
            )}{' '}
            {s.required && <span className="muted small">{t('mfa.required_policy')}</span>}
          </p>
          {s.totp_enabled && <p className="muted small">{t('mfa.recovery_remaining', { n: s.recovery_remaining })}</p>}
          <div className="row">
            {!s.totp_enabled && (
              <button className="btn btn-primary" onClick={() => void startEnable()}>
                {t('mfa.enable')}
              </button>
            )}
            {s.totp_enabled && (
              <button className="btn" onClick={() => setMode('recovery')}>
                {t('mfa.regenerate')}
              </button>
            )}
            {s.totp_enabled && !s.required && (
              <button className="btn" onClick={() => setMode('disable')}>
                {t('mfa.disable')}
              </button>
            )}
          </div>
        </>
      )}

      <Dialog open={mode === 'enable'} title={t('mfa.enroll_title')} onClose={reset}>
        {codes ? (
          <div className="form">
            <RecoveryCodes codes={codes} />
            <button className="btn btn-primary" onClick={reset}>
              {t('mfa.saved_continue')}
            </button>
          </div>
        ) : (
          <form className="form" onSubmit={confirmEnable}>
            <p className="muted">{t('mfa.enroll_body')}</p>
            {setup && <TotpQr uri={setup.otpauth_uri} secret={setup.secret} />}
            <label className="field">
              <span>{t('mfa.first_code')}</span>
              <input className="input mono" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
            {err && <div className="error">{err}</div>}
            <button className="btn btn-primary" type="submit" disabled={busy || !setup}>
              {t('mfa.enable')}
            </button>
          </form>
        )}
      </Dialog>

      <Dialog open={mode === 'recovery'} title={t('mfa.regenerate')} onClose={reset}>
        {codes ? (
          <div className="form">
            <RecoveryCodes codes={codes} />
            <button className="btn btn-primary" onClick={reset}>
              {t('mfa.saved_continue')}
            </button>
          </div>
        ) : (
          <form className="form" onSubmit={submitRecovery}>
            <p className="muted">{t('mfa.regenerate_body')}</p>
            <label className="field">
              <span>{t('mfa.code')}</span>
              <input className="input mono" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
            {err && <div className="error">{err}</div>}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {t('mfa.regenerate')}
            </button>
          </form>
        )}
      </Dialog>

      <Dialog open={mode === 'disable'} title={t('mfa.disable')} onClose={reset}>
        <form className="form" onSubmit={submitDisable}>
          <p className="muted">{t('mfa.disable_body')}</p>
          <label className="field">
            <span>{t('auth.password')}</span>
            <PasswordInput autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('mfa.code')}</span>
            <input className="input mono" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          {err && <div className="error">{err}</div>}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {t('mfa.disable')}
          </button>
        </form>
      </Dialog>
      {emailVerify && <EmailVerifyPrompt resend={emailVerify.resend} onClose={() => setEmailVerify(null)} />}
    </section>
  );
}
